import {StrictMode} from 'react';
import {createRoot} from 'react-dom/client';
import App from './App.tsx';
import ErrorBoundary from './components/ErrorBoundary.tsx';
import './index.css';

// 🛡️ Global DOM Reconciliation Safety Shield (Prevents crashes from Chrome/Google Auto-Translate)
if (typeof window !== 'undefined' && typeof Node !== 'undefined') {
  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function <T extends Node>(child: T): T {
    try {
      return originalRemoveChild.call(this, child) as T;
    } catch (error: any) {
      if (
        error.name === 'NotFoundError' || 
        (error.message && (
          error.message.includes('not a child') || 
          error.message.includes('не є дочірнім') ||
          error.message.includes('не является дочерним')
        ))
      ) {
        console.warn('[DOM Auto-Translate Safety Shield] Suppressed removeChild collision:', error);
        return child;
      }
      throw error;
    }
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function <T extends Node>(newNode: T, referenceNode: Node | null): T {
    try {
      return originalInsertBefore.call(this, newNode, referenceNode) as T;
    } catch (error: any) {
      if (
        error.name === 'NotFoundError' || 
        (error.message && (
          error.message.includes('not a child') || 
          error.message.includes('не є дочірнім') ||
          error.message.includes('не является дочерним')
        ))
      ) {
        console.warn('[DOM Auto-Translate Safety Shield] Suppressed insertBefore collision:', error);
        return newNode;
      }
      throw error;
    }
  };
}

// Intercept long-running /api/run-templated-crew and /api/audit-contract requests to handle asynchronous polling transparently
const originalFetch = window.fetch;
window.fetch = async function(input, init) {
  const url = typeof input === 'string' ? input : (input instanceof URL ? input.toString() : input.url);
  if (url.includes('/api/run-templated-crew') || url.includes('/api/audit-contract')) {
    try {
      const res = await originalFetch(input, init);
      if (!res.ok) return res;

      const data = await res.json();
      if (data.jobId) {
        const jobId = data.jobId;
        // Poll status every 3 seconds
        while (true) {
          await new Promise((resolve) => setTimeout(resolve, 3000));
          try {
            const statusRes = await originalFetch(`/api/crew-status/${jobId}`);
            if (!statusRes.ok) {
              continue; // retry if status request fails temporarily
            }
            const statusData = await statusRes.json();
            if (statusData.status === 'completed') {
              const rawReport = statusData.report || statusData.result || '';
              const reportStr = typeof rawReport === 'string' 
                ? rawReport 
                : (typeof rawReport === 'object' && rawReport !== null 
                    ? (rawReport.report || rawReport.result || JSON.stringify(rawReport, null, 2)) 
                    : String(rawReport));

              return new Response(JSON.stringify({ report: reportStr }), {
                status: 200,
                headers: { 'Content-Type': 'application/json' }
              });
            } else if (statusData.status === 'failed') {
              return new Response(JSON.stringify({ error: statusData.error || 'Помилка під час аналізу.' }), {
                status: 500,
                headers: { 'Content-Type': 'application/json' }
              });
            }
          } catch (e) {
            // Ignore temporary network errors during polling
          }
        }
      }
      return new Response(JSON.stringify(data), {
        status: res.status,
        headers: res.headers
      });
    } catch (err: any) {
      return new Response(JSON.stringify({ error: err.message || 'Помилка мережі.' }), {
        status: 500,
        headers: { 'Content-Type': 'application/json' }
      });
    }
  }
  return originalFetch(input, init);
};

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </StrictMode>,
);

if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
      .then((reg) => {
        console.log('Service Worker registered successfully with scope:', reg.scope);
      })
      .catch((err) => {
        console.error('Service Worker registration failed:', err);
      });
  });
}
